import { useEffect, useState } from "react";

import { usePrettyLogEventPage } from "@components/v2/analytics/hooks";
import { Button } from "@components/v2/buttons/button";
import { Body } from "@components/v2/typography/body";
import { VALIDATE_EXPORT_TARGET_V2 } from "@features/data-export-v2/data-export-v2-queries";
import { useNorthbeamMutation } from "@hooks/http";
import {
  type ValidateExportTargetV2,
  type ValidateExportTargetV2Variables,
} from "@north-beam/nb-client-side";

type S3BucketValidationProps = {
  bucketName: string;
  awsRole: string;
  isBucketValid: boolean;
  onValidated?(): void;
  onValidationError?(errorMessage: string): void;
};

export const S3BucketValidation = ({
  bucketName,
  awsRole,
  isBucketValid,
  onValidated,
  onValidationError,
}: S3BucketValidationProps) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const [validationError, setValidationError] = useState<string | null>(null);

  const canRunBucketValidation = bucketName && awsRole;

  useEffect(() => {
    setValidationError(null);
  }, [bucketName, awsRole]);

  const [validateExportTargetV2, { loading }] = useNorthbeamMutation<
    ValidateExportTargetV2,
    ValidateExportTargetV2Variables
  >(VALIDATE_EXPORT_TARGET_V2);

  const onValidateBucketClick = async () => {
    if (!canRunBucketValidation || isBucketValid) {
      return;
    }

    prettyLogEventPage({
      domain: "DataExportV2",
      action: "onClick",
      additionalActionInfo: "Validate external S3 bucket access",
      data: {
        pageContext: { bucketName, awsRole },
      },
    });

    const result = await validateExportTargetV2({
      variables: { data: { type: "S3", bucketName, awsRole } },
    });

    const accessValidationResult = result.data?.validateExportTargetV2;
    const status = accessValidationResult?.status;

    if (status === "success") {
      onValidated?.();
      return;
    }

    const errorMessage =
      accessValidationResult?.message ?? "Error validating bucket";
    onValidationError?.(errorMessage);

    setValidationError(errorMessage);
  };

  return (
    <div className="flex gap-2 items-center">
      <Button
        variant="secondary"
        className="w-fit"
        onClick={onValidateBucketClick}
        loading={loading}
        disabled={!canRunBucketValidation}
      >
        {isBucketValid ? "Validated!" : "Validate Bucket"}
      </Button>
      {validationError && (
        <Body variant={300} color="error">
          {validationError}
        </Body>
      )}
    </div>
  );
};
